import { makeStyles } from '@material-ui/core/styles';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  type Entity,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { EntityInfoCard, EntityRefLink } from '@backstage/plugin-catalog-react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { catalogGraphUrl } from './EntityHeaderActions';
import { RAIL_EYEBROW } from './railEyebrow';

/*
 * The mock's Relations card: one compact card in the rail with a labelled chip
 * row per relation type, replacing the full-width Components / Provided APIs /
 * Consumed APIs tables that used to run down the main column.
 *
 * Measured from the mock's own inline styles:
 *
 *   body    padding:14px 20px; flex column; gap:12px
 *   section flex column; gap:7px
 *   label   11px #6d6d6d
 *   chips   flex; gap:6px; wrap
 *   chip    DM Mono 11px #202020; background:#f4f4f4; radius:6px; padding:3px 8px
 *
 * The header's "View relations" link opens the catalog graph pre-filtered to
 * this entity (`catalogGraphUrl`) — it used to be a page-level header button
 * on `EntityHeaderActions`, but it reads relationship data, so it lives here
 * next to the relations it's a deeper view of.
 *
 * Ownership and membership relations are deliberately absent — `ownedBy` is
 * already the About card's Owner field, and the Group page states membership
 * through the Ownership and members cards.
 */

/** Relation types the card renders, in the order the mock reads them. */
const SECTIONS: ReadonlyArray<{ type: string; label: string }> = [
  { type: RELATION_PART_OF, label: 'Part of' },
  { type: RELATION_HAS_PART, label: 'Has part' },
  { type: RELATION_DEPENDS_ON, label: 'Depends on' },
  { type: RELATION_DEPENDENCY_OF, label: 'Dependency of' },
  { type: RELATION_PROVIDES_API, label: 'Provides' },
  { type: RELATION_CONSUMES_API, label: 'Consumes' },
  { type: RELATION_API_PROVIDED_BY, label: 'Provided by' },
  { type: RELATION_API_CONSUMED_BY, label: 'Consumed by' },
];

const RENDERED_TYPES = new Set(SECTIONS.map(s => s.type));

/** Whether the entity has any relation this card would render. */
export function hasRenderableRelations(entity: Entity): boolean {
  return Boolean(entity.relations?.some(r => RENDERED_TYPES.has(r.type)));
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: RAIL_EYEBROW,
  chips: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  /*
   * The chip is the anchor, so the whole pill is the hit target. Colour is not
   * set: these read as entity tokens rather than links, which is the mock's
   * treatment — ink on the inset surface, not link blue.
   */
  chip: {
    '& a': {
      display: 'inline-block',
      maxWidth: '100%',
      fontFamily: 'var(--bui-font-monospace)',
      fontSize: 11,
      lineHeight: 1.5,
      color: 'var(--bui-fg-primary)',
      background: 'var(--hone-portal-fill-inset)',
      borderRadius: 6,
      padding: '3px 8px',
      textDecoration: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& a:hover': {
      background: 'var(--bui-border-1)',
      color: 'var(--bui-fg-primary)',
      textDecoration: 'none',
    },
  },
  /** Matches HomePanel's "View all ▸" header link treatment. */
  viewGraph: {
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
});

export function EntityRelationsCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const relations = entity.relations ?? [];

  const sections = SECTIONS.map(({ type, label }) => ({
    label,
    targets: relations.filter(r => r.type === type).map(r => r.targetRef),
  })).filter(s => s.targets.length > 0);

  if (sections.length === 0) {
    return null;
  }

  const headerActions = (
    <Link className={classes.viewGraph} to={catalogGraphUrl(entity)} underline="none">
      View relations ▸
    </Link>
  );

  return (
    <EntityInfoCard title="Relations" headerActions={headerActions}>
      <div className={classes.root}>
        {sections.map(({ label, targets }) => (
          <div className={classes.section} key={label}>
            <span className={classes.label}>{label}</span>
            <div className={classes.chips}>
              {targets.map(targetRef => (
                <span className={classes.chip} key={targetRef}>
                  <EntityRefLink entityRef={targetRef} />
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </EntityInfoCard>
  );
}
